// The /me route tree: /me (layout), the main frame with the Me sidebar (the
// index and every part's routes), and /me/settings with its own sidebar.
//
// Promise:
// - Parts add routes under the main frame (`routes(main)`): "cases" becomes
//   /me/cases. Paths name what is open (/me/cases/<id>); view state never goes
//   into the URL (see the URL rules of the part design). A part's bare
//   routes (`bareRoutes(me)`) sit under /me without the frame.
// - /me itself goes to the first nav item, or shows an empty Me when there is none.
// - Opening Me with router state `{ label }` narrows Me to that label.
// - A settings item is a page (/me/settings/<id>), or, when it is a list
//   (`screen`), the list with what is open in it as a path
//   (/me/settings/<id>/<sub>).

import type { QueryClient } from "@tanstack/react-query";
import {
  type AnyRoute,
  createRoute,
  notFound,
  Outlet,
  redirect,
  useParams,
} from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import type { Api } from "../data/api";
import { favoritesQuery } from "../me/favorites";
import { profileQuery } from "../me/profile";
import {
  SettingsDockUp,
  SettingsIndex,
  SettingsItemPage,
  SettingsSidebar,
} from "../settings/settings-page";
import { MeSidebarCollapsed } from "../shell/collapsed-sidebar";
import { SETTINGS_WIDTH } from "../shell/layout";
import { MeShell } from "../shell/me-shell";
import { PanelBody, PanelHeader } from "../shell/panel";
import { MeSidebar } from "../shell/sidebar";
import { type MeApp, useMeApp } from "./context";

export interface MeRouterContext {
  queryClient: QueryClient;
  api: Api;
}

export function meRouteTree(root: AnyRoute, app: MeApp): AnyRoute {
  const me = createRoute({
    getParentRoute: () => root,
    path: "me",
    beforeLoad: ({ location }) => {
      const workspace = (location.state as { workspace?: unknown } | undefined)
        ?.workspace;
      if (typeof workspace === "string" || workspace === null)
        app.workspaceFilter.set(workspace);
    },
    loader: ({ context }) => {
      const c = context as MeRouterContext;
      return c.queryClient.ensureQueryData(profileQuery(c.api));
    },
    component: Outlet,
    notFoundComponent: MeNotFound,
  });

  const main = createRoute({
    getParentRoute: () => me,
    id: "main",
    // The sidebar's favorites are drawn with their rows from the first frame,
    // so the blocks below them never move down when the list arrives. A failed
    // load is left to the section (Me still opens).
    loader: async ({ context }) => {
      if (Object.keys(app.favoriteKinds).length === 0) return;
      const c = context as MeRouterContext;
      await c.queryClient
        .ensureQueryData(favoritesQuery(c.api, "me"))
        .catch(() => undefined);
    },
    component: MainFrame,
  });

  const home = createRoute({
    getParentRoute: () => main,
    path: "/",
    beforeLoad: () => {
      const first = app.nav[0];
      if (first) throw redirect({ to: first.to, replace: true });
    },
    component: EmptyMe,
  });

  const settings = createRoute({
    getParentRoute: () => me,
    path: "settings",
    notFoundComponent: SettingsNotFound,
    // Folds and narrows to its icons as Me's sidebar does (the same kept width
    // and folding). On a phone there is no sidebar: the Dock, as on Me's
    // pages, and the index lists the items.
    component: SettingsFrame,
  });
  const settingsIndex = createRoute({
    getParentRoute: () => settings,
    path: "/",
    component: SettingsIndex,
  });
  const settingsItem = createRoute({
    getParentRoute: () => settings,
    path: "$item",
    loader: ({ params }) => {
      if (!app.settings.some((s) => s.id === params.item)) throw notFound();
    },
    component: function SettingsItemRoute() {
      const { item } = settingsItem.useParams();
      const def = app.settings.find((s) => s.id === item);
      if (!def) return null;
      // An item with a list draws the panel (its detail reads `sub`); one
      // with pages below it draws the page.
      if (def.screen || def.pages) return <def.Component />;
      return <SettingsItemPage item={def} />;
    },
  });
  // Below an item that draws the panel or has pages: nothing open, or the one
  // open (only such items have anything below them).
  const settingsItemIndex = createRoute({
    getParentRoute: () => settingsItem,
    path: "/",
    component: () => null,
  });
  const settingsItemSub = createRoute({
    getParentRoute: () => settingsItem,
    path: "$sub",
    loader: ({ params }) => {
      const def = app.settings.find((s) => s.id === params.item);
      if (!def?.screen && !def?.pages) throw notFound();
    },
    component: () => null,
  });

  const partRoutes = app.parts.flatMap((p) => p.routes?.(main) ?? []);
  const bareRoutes = app.parts.flatMap((p) => p.bareRoutes?.(me) ?? []);
  return me.addChildren([
    ...bareRoutes,
    main.addChildren([home, ...partRoutes]),
    settings.addChildren([
      settingsIndex,
      settingsItem.addChildren([settingsItemIndex, settingsItemSub]),
    ]),
  ]);
}

function NotFoundPanel() {
  return (
    <>
      <PanelHeader title={m.shell_not_found_title()} />
      <PanelBody>
        <p className="text-[13px] text-muted">{m.shell_not_found_body()}</p>
      </PanelBody>
    </>
  );
}

const noSwap = () => null;

/** Me's frame, with Me's sidebar or the one the host puts in its place. */
function MainFrame() {
  const swap = (useMeApp().extensions.useSidebar ?? noSwap)();
  return (
    <MeShell
      sidebar={swap?.sidebar ?? <MeSidebar />}
      collapsed={swap?.collapsed ?? <MeSidebarCollapsed />}
      label={swap?.label}
    >
      <Outlet />
    </MeShell>
  );
}

/**
 * A path that names nothing, inside Me's frame: the sidebar stays, so there is
 * a way on (a bare page had no sidebar and no way back).
 */
export function MeNotFound() {
  return (
    <MeShell sidebar={<MeSidebar />} collapsed={<MeSidebarCollapsed />}>
      <NotFoundPanel />
    </MeShell>
  );
}

/**
 * The settings' frame. Its pages are a reading column, not a document beside
 * a list: the sidebar folds only when that column has no room. An item that
 * draws a list and its detail (`screen`) keeps a document's room.
 */
function SettingsFrame() {
  const { settings } = useMeApp();
  const item = useParams({ strict: false }) as { item?: string };
  const wide = settings.some((s) => s.id === item.item && s.screen);
  return (
    <MeShell
      sidebar={<SettingsSidebar />}
      collapsed={<SettingsSidebar folded />}
      foldByWidthOnly
      menu="settings"
      list={wide}
      content={wide ? undefined : SETTINGS_WIDTH}
    >
      <SettingsDockUp index="/me/settings" />
      <Outlet />
    </MeShell>
  );
}

/** A settings item that is not here (the other edition's), in the settings frame. */
function SettingsNotFound() {
  return (
    <MeShell
      sidebar={<SettingsSidebar />}
      collapsed={<SettingsSidebar folded />}
      foldByWidthOnly
      menu="settings"
    >
      <SettingsDockUp index="/me/settings" />
      <NotFoundPanel />
    </MeShell>
  );
}

function EmptyMe() {
  return (
    <>
      <PanelHeader title={m.shell_me()} />
      <PanelBody>
        <p className="text-[13px] text-faint">{m.shell_me_empty()}</p>
      </PanelBody>
    </>
  );
}
